import Facebook from '../providers/Facebook';
import Instagram from '../providers/Instagram';
import Twitter from '../providers/Twitter';
import Youtube from '../providers/Youtube';

import { validate } from '../utils/formUtils';

/**
 * Privacy Management Note: Campaign Authentication creates a few cookies as part of the redirection process after
 * authentication with the social media app.  They are considered strictly necessary cookies since they are required
 * for the authentication process to work corretly.  The cookies are deleted after the login process completes.
 * Since the cookies are 'strictly necessary', they are not gated.
 * The cookies are:
 * - ngx.auth.redirect
 * - ngx.auth.redirect.provider
 * - ngx.auth.redirect.responseObject
 */

export default (function(w, d, $, _, undefined) {
  // Internal Properties.
  var _config = {
      // Valid networks.
      validNetworks:      ['facebook', 'twitter', 'youtube', 'instagram', 'googleplus'],
      authRedirectParams: 'authredirect=true',
    },
    _state = {
      verifyAuthedCalled: false,
    },
    _user = null,
    _event = null,
    _api = null,
    _embed = null,
    _lang = null,
    _context = null,
    _appState = null,
    _appConfig = null,
    _storageManager = null,
    _activeAuthStrategyKey = null,
    _activeAuthStrategyDelegate = null,
    _activeProviderDelegate = null,
    _callbacks = {},
    _authRedirectData = null,
    // Init
    init = function(config) {
      // Merge config.
      _.extend(_config, config);

      // Convenience shortcuts.
      _user = NGX.User;
      _event = NGX.App.Events;
      _api = NGX.App.api;
      _embed = NGX.Embed;
      _lang = NGX.lang;
      _context = NGX.context;
      _appState = NGX.App.state;
      _appConfig = NGX.App.config;
      _storageManager = NGX.StorageManager;

      // Init user object.
      NGX.User.init({ validNetworks: _config.validNetworks });

      // Always init core social auth functionality if network config is present. This means
      // social auth login API can still be used even when there is no explicit social strategy defined for
      // the campaign.
      coreSocialAuth.init();

      // Additionally, init appropriate delegate based on auth strategy.
      switch (_config.strategy) {
        // Social Sign In
        case 'strategy.socialauth.socialsignin':
          _activeAuthStrategyKey = 'social';

          break;

        // Custom Authentication
        case 'strategy.socialauth.customauthentication':
          _activeAuthStrategyKey = 'custom';

          break;

        // Disabled.
        case 'strategy.socialauth.disabled':
        default:
          _activeAuthStrategyKey = 'disabled';

          break;
      }

      _activeAuthStrategyDelegate = authStrategyDelegates[_activeAuthStrategyKey];
      _activeAuthStrategyDelegate.init();

      // Handle an auth redirect if required.
      var queryString = _api.getQuerystring();
      if (queryString.indexOf(_config.authRedirectParams) !== -1) {
        handleAuthRedirect();
      }

      // Register custom handler with EntryForm module (if EntryForm module is present).
      if (NGX.entryForm) {
        NGX.entryForm.registerCustomSubmitHandler('verifyAuthed', verifyAuthed);
      }
    },
    enableNetworks = function(networks) {
      coreSocialAuth.enableNetworks(networks);
    },
    // Authenticate user against required provider.
    login = function(providerId, options) {
      // Process options.
      if (util.isDefined(options)) {
        util.processOptions(options);
      }

      var error = null;

      // Validate providerId.
      if (util.validateProvider(providerId)) {
        // Execute corresponding auth strategy login delegate.
        if (_activeAuthStrategyDelegate.hasOwnProperty('login')) {
          _activeAuthStrategyDelegate.login(providerId);
        } else {
          // Or alternatively fall back to core social auth.
          coreSocialAuth.login(providerId);
        }
      } else {
        error += 'Invalid provider: ' + providerId + ' (provider id invalid, or provider not initialized)';
      }

      // If errors encountered.
      if (error !== null) {
        // Call fail callback, if supplied.
        if (util.isDefined(_callbacks.fail)) {
          _callbacks.fail(error);
        }
      }
    },
    // Ensure that user is authed if required.
    verifyAuthed = function() {
      var authed = true;

      // If we are using any strategy other than disabled.
      if (_config.strategy !== 'strategy.socialauth.disabled') {
        // And only if enforcement is required.
        if (_config.enforcement === 'strategy.socialauthenforcement.required') {
          // Ask delegate if we are authed.
          if (_activeAuthStrategyDelegate.hasOwnProperty('verifyAuthed')) {
            authed = _activeAuthStrategyDelegate.verifyAuthed();
          }

          // If not authed.
          if (!authed) {
            if (_activeAuthStrategyDelegate.hasOwnProperty('handleNotAuthed')) {
              _activeAuthStrategyDelegate.handleNotAuthed();
            }
          }
        }
      }

      // Store the fact that we have previously attempted to verify the users authed state.
      _state.verifyAuthedCalled = true;

      return authed;
    },
    // Handle an auth redirect.
    handleAuthRedirect = function() {
      try {
        // Attempt to retrieve data from localstorage/cookie.
        var provider = _storageManager.get('ngx.auth.redirect.provider');
        var responseObject = JSON.parse(_storageManager.get('ngx.auth.redirect.responseObject'));

        // If present on the redirect url, use query string params as override.
        var queryString = _api.getQuerystring();

        var responseObjectQs = parseQueryString(queryString, 'authresponse');
        if (responseObjectQs) {
          responseObject = JSON.parse(responseObjectQs);
        }

        var providerQs = parseQueryString(queryString, 'provider');
        if (providerQs) {
          provider = providerQs;
        }

        // Store data.
        _authRedirectData = {
          provider:       provider,
          responseObject: responseObject,
        };

        // Clear up storage.
        clearStorage();

        // Dispatch an event so that any other parts of the system can respond if required.
        setTimeout(function() {
          _event.trigger('auth:redirect', _authRedirectData.provider);
        }, 1);
      } catch (exception) {}
    },
    // Clear up storage.
    clearStorage = function() {
      try {
        _storageManager.clear('ngx.auth.redirect');
        _storageManager.clear('ngx.auth.redirect.provider');
        _storageManager.clear('ngx.auth.redirect.responseObject');
      } catch (exception) {}
    },
    parseQueryString = function(queryString, needle) {
      needle = needle.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
      var regex = new RegExp('[\\?#&]' + needle + '=([^&#]*)'),
        results = regex.exec(queryString);

      return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
    },
    // Delegate auth strategy handlers.
    authStrategyDelegates = {
      // Social Sign In.
      social: (function() {
        var rendered = false;

        var init = function() {
            render();

            // Listen for auth redirects.
            _event.on('auth:redirect', function(providerId) {
              login(providerId);
            });
          },
          render = function() {
            if (!rendered) {
              if (util.campaignFormExists()) {
                // Init the view.
                view.render.init();

                // Output buttons for each provider.
                renderButtons();

                // Attach event listeners.
                $('.xAuthButton').on('click', handleLoginButtonClicked);

                // Set rendered flag.
                rendered = true;

                // Send out ready event.
                util.setAuthReady();
              }
            }
          },
          renderButtons = function() {
            // If campaign is inside Facebook, only output the Facebook button.
            var isFacebookContext = _context.isFB || _context.isFBTab ? true : false;

            // Iterate over providers in preffered order.
            var providerOrder = [
              _config.validNetworks[0],
              _config.validNetworks[2],
              _config.validNetworks[3],
              _config.validNetworks[1],
            ];
            _.each(providerOrder, function(providerId) {
              // Check config for current provider has been supplied.
              if (
                _config.networks.hasOwnProperty(providerId) &&
                _config.socialSignInNetworks.hasOwnProperty(providerId)
              ) {
                // Check that provider is enabled.
                var enabled = _config.socialSignInNetworks[providerId].hasOwnProperty('enabled') ?
                  _config.socialSignInNetworks[providerId].enabled :
                  false;
                if (enabled) {
                  // Skip all non Facebook buttons if required.
                  if (isFacebookContext) {
                    if (providerId !== 'facebook') {
                      return;
                    }
                  }

                  // Render button with empty label.
                  const tooltipText = NGX.lang.get(`socialNetworkHints.${providerId}`);
                  view.render.button(util.ucFirst(providerId), '', true, tooltipText);
                }
              }
            });
          },
          handleLoginButtonClicked = function(event) {
            // Clicked element.
            var clickedEl = event.currentTarget;

            // Get the data attributes for the clicked auth button.
            var authData = $(clickedEl).data();

            var providerId = authData.ngxProvider;

            // Call login method.
            login(providerId);
          },
          login = function(providerId) {
            // Verify that the value of NGX.App.config.displayURL === current document location, if it doesn't then the sign in flow will
            // fail (eg. campaign is being viewed in staging preview mode via the console domain). Show a warning in this situation.
            if (window.location.href.indexOf(_appConfig.displayURL) === -1) {
              NGX.overlay.create({ content: _lang.userAuth.signIn.usePublishedUrlMessage });
              return false;
            }

            // Show activity.
            view.render.authenticating();

            // Proxy via core social auth login.
            coreSocialAuth.login(providerId, handleLoginSuccess, handleLoginFail);
          },
          handleLoginSuccess = function(networkData) {
            // Proxy via core social auth method.
            coreSocialAuth.handleLoginSuccess(networkData, handleIdentifySuccess, handleIdentifyFail);

            // Fire event message to parent frame via Embed API.
            _api.messageParent({
              action:  'event',
              event:   'auth:login:success',
              payload: {},
            });

            // Show success message.
            view.render.success();
          },
          handleLoginFail = function(error) {
            // Proxy via core social auth method.
            coreSocialAuth.handleLoginFail();

            // Fire event message to parent frame via Embed API.
            _api.messageParent({
              action:  'event',
              event:   'auth:login:fail',
              payload: {},
            });

            // Show failure message.
            view.render.fail(error);
          },
          handleIdentifySuccess = function(userData) {
            // Proxy via core social auth method.
            coreSocialAuth.handleIdentifySuccess(userData);

            // Fire event message to parent frame via Embed API.
            _api.messageParent({
              action:  'event',
              event:   'auth:identify:success',
              payload: { user: _user.get({ filterExternal: true }) },
            });

            // Update form.
            view.util.populateFormFields();
          },
          handleIdentifyFail = function() {
            // Proxy via core social auth method.
            coreSocialAuth.handleIdentifyFail();

            // Fire event message to parent frame via Embed API.
            _api.messageParent({
              action:  'event',
              event:   'auth:identify:fail',
              payload: { user: _user.get() },
            });
          },
          verifyAuthed = function() {
            var authed = false;
            var user = _user.get();
            authed = user.authed;

            return authed;
          },
          handleNotAuthed = function() {
            // Show error message.
            view.render.notAuthed();
          };

        return {
          init:  init,
          login: login,

          verifyAuthed:    verifyAuthed,
          handleNotAuthed: handleNotAuthed,
        };
      })(),

      // Custom Authentication.
      custom: (function() {
        var rendered = false;

        var init = function() {
            registerListeners();

            // If we are not in preview mode, then render.
            if (!_context.isPreview) {
              render();
            }
          },
          registerListeners = function() {
            // Register for the login events.
            _embed.registerEventHandler('auth:login:success', handleLoginSuccess);
            _embed.registerEventHandler('auth:login:fail', handleLoginFail);
            _embed.registerEventHandler('auth:login:required', handleLoginRequired);
          },
          render = function() {
            if (!rendered) {
              if (util.campaignFormExists() && isFramed()) {
                // Init the view.
                view.render.init();

                // Output button.
                renderButtons();

                // Attach event listeners.
                $('.xAuthButton').on('click', handleLoginButtonClicked);

                // Show authenticating message.
                view.render.authenticating();

                // Set rendered flag.
                rendered = true;

                // Send out ready event.
                util.setAuthReady();
              }
            }
          },
          renderButtons = function() {
            // Render button.
            view.render.button('Custom', _lang.userAuth.signIn.customAuthButton, false);
          },
          handleLoginButtonClicked = function(event) {
            // Clicked element.
            var clickedEl = event.currentTarget;

            // Get the data attributes for the clicked auth button.
            var authData = $(clickedEl).data();

            var providerId = authData.ngxProvider;

            // Call login method.
            login(providerId);
          },
          login = function() {
            // Show activity.
            view.render.authenticating();

            // Send event message to parent frame via Embed API.
            _api.messageParent({
              action:  'event',
              event:   'auth:action:login',
              payload: {},
            });
          },
          handleLoginSuccess = function(e, payload, msgId) {
            var user = payload.user;

            // Extract the network.
            var network = '';
            if (util.isDefined(user.network) && util.hasValue(user.network)) {
              network = user.network.toLowerCase();

              // Alias Youtube to Googleplus.
              if (network === 'youtube') {
                network = 'googleplus';
              }
            }

            // Merge custom network id.
            user.associatedData.ngxId = network;

            // Attempt to store user.
            var userStored = _user.set(user);

            // If user was successfully stored.
            if (userStored) {
              // Store auth success.
              _user.setAuthed(true);

              // Show success message.
              view.render.success();

              // Update form.
              view.util.populateFormFields();

              // Dispatch internal event in case we want to handle any custom user related logic.
              _event.trigger('auth:login:custom:userstored', _user);

              // Send success callback message.
              _api.messageParent({
                action:     'loginusercallback',
                callbackId: msgId,
                payload:    {
                  success: true,
                  user:    _user.get({ filterExternal: true }),
                },
              });
            } else {
              // Otherwise...
              // Get last error.
              var error = _user.getError();

              // Dispatch internal event in case we want to handle any custom user related logic.
              _event.trigger('auth:login:custom:userrejected', error);

              // Send fail callback message.
              _api.messageParent({
                action:     'loginusercallback',
                callbackId: msgId,
                payload:    {
                  success: false,
                  error:   error,
                },
              });

              // Emit fail event also.
              _api.messageParent({
                action:  'event',
                event:   'auth:login:fail',
                payload: {
                  error: error,
                },
              });
            }
          },
          handleLoginFail = function(e, payload) {
            // Store auth fail.
            _user.setAuthed(false);

            // Get the error message.
            var error = payload.error;

            // Show failure message.
            view.render.fail(error);
          },
          handleLoginRequired = function(e, payload) {
            view.render.prompt();
          },
          verifyAuthed = function() {
            var authed = false;

            // If we are not framed, then always assume authed.
            if (!isFramed()) {
              authed = true;
            } else {
              var user = _user.get();
              authed = user.authed;
            }

            return authed;
          },
          handleNotAuthed = function() {
            // Show error message.
            view.render.notAuthed();
          },
          isFramed = function() {
            // Check if we are inside a frame AND NOT inside Facebook.
            var framed = false;

            if (_appState.framed === true && _context.isFB !== true) {
              framed = true;
            }

            return framed;
          };

        return {
          init:  init,
          login: login,

          verifyAuthed:    verifyAuthed,
          handleNotAuthed: handleNotAuthed,
        };
      })(),

      // Disabled.
      disabled: (function() {
        var init = function() {};

        return {
          init: init,
        };
      })(),
    },
    // Core social auth functionality. Abstracted away from any specific auth strategy.
    coreSocialAuth = (function() {
      var init = function() {
          _state.initializedProviders = [];
        },
        enableNetworks = function(networks) {
          var enableNetworks = [];

          if (_.isString(networks)) {
            if (networks === 'all') {
              // Ensure networks key is present (used to always be present,
              // but now will only be present if either claim my content is enabled or sign in is enabled at account level).
              if (_config.networks) {
                enableNetworks = Object.keys(_config.networks);
              }
            }
          } else if (_.isArray(networks)) {
            enableNetworks = networks;
          }

          // Init all providers defined in config.
          _.each(enableNetworks, function(networkKey) {
            if (_config.networks.hasOwnProperty(networkKey)) {
              var providerId = networkKey;
              var providerConfig = _config.networks[networkKey];

              // Sanitize providerId.
              var providerIdSan = util.sanitizeProviderId(providerId);

              // Get provider object.
              var provider = NGX.Auth.Providers[providerIdSan];

              // Boolean to indicate if we should enable this provider based on some additional common logic.
              var enableProvider = false;

              // If account level social sign in config is present, additionally verify that the network is also enabled there.
              if (_config.socialSignInNetworks) {
                if (
                  _config.socialSignInNetworks[networkKey] &&
                  _config.socialSignInNetworks[networkKey].enabled === true
                ) {
                  enableProvider = true;
                }
                // If no account level social sign in config is present (this can occur for some campaign types eg. Claim Content).
              } else {
                // Check if network has an explicit account resriction, if it does, do not enable it.
                if (provider.accountRestricted !== true) {
                  enableProvider = true;
                }
              }

              // Finally, if we need to, init the provider class.
              if (enableProvider) {
                // Init and inject required params.
                provider.init(providerConfig);

                // Store initialized provider key in state.
                _state.initializedProviders.push(providerIdSan);
              }
            }
          });
        },
        login = function(providerId, callbackSuccess, callbackFail) {
          // Check if current browser/device is supported.
          if (util.isSupportedBrowser()) {
            // Sanitize providerId.
            var providerIdKey = util.sanitizeProviderId(providerId);

            // Get handle on corresponding provider delegate.
            var provider = NGX.Auth.Providers[providerIdKey];

            // Store reference to active provider delegate.
            _activeProviderDelegate = provider;

            // Use override callback functions, or default.
            var useCallbackSuccess = util.isDefined(callbackSuccess) ? callbackSuccess : handleLoginSuccess;
            var useCallbackFail = util.isDefined(callbackFail) ? callbackFail : handleLoginFail;

            // Store redirect information in localstorage/cookie, this will only be used if the auth window ends up in a state whereby the
            // browser redirected the campaign page, rather than opened a new window. This can happen when a user is browsing a campaign
            // using an in-app browser on IOS (eg Facebook app, Twitter app) and possibly other situations too.
            var redirectUrl = util.appendAuthRedirectParams(window.location.href, providerIdKey);

            // Set shorter cookie expiry time for auth related cookies (if not supplied then StorageService defaults to 360 days!).
            var expires = new Date();
            expires.setTime(expires.getTime() + 30 * 60 * 1000); // 30 mins

            _storageManager.set('ngx.auth.redirect', redirectUrl, null, expires);
            _storageManager.set('ngx.auth.redirect.provider', providerIdKey, null, expires);

            // Execute call to providers login method, inject required callbacks.
            _activeProviderDelegate.login(useCallbackSuccess, useCallbackFail, redirectUrl, _authRedirectData);
          } else {
            view.render.unsupported();
          }
        },
        handleLoginSuccess = function(networkData, callbackIdentifySuccess, callbackIdentifyFail) {
          // Store auth success.
          _user.setAuthed(true);

          // Store authed network data.
          _user.addAuthedNetwork(networkData);

          // Use override callback functions, or default.
          var callbackSuccess = util.isDefined(callbackIdentifySuccess) ?
            callbackIdentifySuccess :
            handleIdentifySuccess;
          var callbackFail = util.isDefined(callbackIdentifyFail) ? callbackIdentifyFail : handleIdentifyFail;

          // Execute call to providers identify method, inject required callbacks.
          var user = _user.get();
          _activeProviderDelegate.identify(user, callbackSuccess, callbackFail);

          // Clear up storage.
          clearStorage();
        },
        handleLoginFail = function() {
          // Store auth failure.
          _user.setAuthed(false);

          // Always call global callbacks.
          if (util.isDefined(_callbacks.fail)) {
            _callbacks.fail();
          }

          // Clear up storage.
          clearStorage();
        },
        handleIdentifySuccess = function(userData) {
          // Set current user details.
          _user.setId(userData.data.id);
          _user.setFirstName(userData.data.firstName);
          _user.setLastName(userData.data.lastName);
          _user.setEmail(userData.data.email);

          // Store associated data.
          _user.addAssociatedData(userData.data.associatedData);

          // Store history against network key.
          _user.addAuthedNetworkUser(userData);

          // Always call global callbacks.
          if (util.isDefined(_callbacks.success)) {
            _callbacks.success(_user.get());
          }
        },
        handleIdentifyFail = function() {};

      return {
        init:                  init,
        enableNetworks:        enableNetworks,
        login:                 login,
        handleLoginSuccess:    handleLoginSuccess,
        handleLoginFail:       handleLoginFail,
        handleIdentifySuccess: handleIdentifySuccess,
        handleIdentifyFail:    handleIdentifyFail,
      };
    })(),
    // View / rendering.
    view = {
      template: {
        compile: {
          container: function() {
            // Define params to pass to Handlebars template.
            var params = {
              headline: _lang.userAuth.signIn.headline,
            };

            // Get Handlebars template.
            var containerTemplate = NGX.templates.userAuthContainer(params);

            return containerTemplate;
          },
        },
      },

      render: {
        init: function() {
          // Based on the display mode, render accordingly.
          switch (_config.display) {
            // Form
            case 'display.socialauth.form':
              view.render.asForm();

              break;

            // Gate
            case 'display.socialauth.gate':
              view.render.asGate();

              break;
          }

          // Hide default prompts and messages.
          $('#xUserAuthContainer .xPrompt, #xUserAuthContainer .xMessageContainer').hide();

          // Show corresponding prompt for strategy.
          view.render.prompt();
        },

        asForm: function() {
          // Compile Handlebars template.
          var containerTemplate = view.template.compile.container();

          // Depending on campaign type, behave accordingly:
          var campaignType = _appConfig.type;
          switch (campaignType) {
            // For quizes.
            case 'type.campaignobject.quiz':
              // Attach to last page inside form container.
              $('#xCampaignForm .xFormPages .xFormPage.last').prepend(containerTemplate);

              break;

            // For all other types.
            default:
              // Attach to form container.
              $('#xCampaignForm').before(containerTemplate);

              break;
          }
        },

        asGate: function() {
          // Compile Handlebars template.
          var containerTemplate = view.template.compile.container();

          // Define overlay config object.
          var overlayConfig = {
            content: containerTemplate,
          };

          // Set type: modal and disableUIClose: true, to stop user from closing overlay if enforcement mode is required.
          if (_config.enforcement === 'strategy.socialauthenforcement.required') {
            overlayConfig.type = 'modal';
            overlayConfig.disableUIClose = true;
          }

          // Create overlay.
          NGX.overlay.create(overlayConfig);
        },

        authenticating: function() {
          // Set authenticating message.
          view.render.message(_lang.userAuth.signIn.authenticatingUserMessage);

          // Show/hide activity elements.
          $('#xUserAuthContainer .xIntroContainer, #xUserAuthContainer .xAuthButtonsContainer').hide();
          $('#xUserAuthContainer .xMessagesContainer .activityContainer').show();
        },

        success: function() {
          // Hide authenticating elements.
          $('#xUserAuthContainer .xMessagesContainer .activityContainer').hide();

          // Show success message.
          view.render.message(_lang.userAuth.signIn.loginSuccessMessage);

          // If overlay open, close after short timeout.
          if (_config.display === 'display.socialauth.gate') {
            var overlayId = 'xO-1';
            if ($('#' + overlayId).length > 0) {
              setTimeout(function() {
                NGX.overlay.get(overlayId).destroy();
              }, 2000);
            }
          }
        },

        fail: function(error) {
          // Hide authenticating elements.
          $('#xUserAuthContainer .xMessagesContainer .activityContainer').hide();

          // Re-show login prompt.
          $('#xUserAuthContainer .xIntroContainer, #xUserAuthContainer .xAuthButtonsContainer').show();

          // Set error.
          var errorMessage =
            util.isDefined(error) && error !== null ? error : _lang.userAuth.signIn.loginFailureMessage;

          // Show failure message.
          view.render.error(errorMessage);
        },

        notAuthed: function() {
          // Show error message.
          view.render.error(_lang.userAuth.signIn.loginRequiredMessage);
        },

        button: function(providerId, label, hasIcon, tooltip) {
          const btnParams = {
            providerId:        providerId,
            providerName:      providerId,
            buttonLabel:       label,
            hasIcon:           hasIcon,
            additionalClasses: !label ? 'xEmptyLabel' : '',
            tooltipText:       tooltip || ''
          };
          var compiledTemplate = NGX.templates.userAuthButton(btnParams);

          $('#xUserAuthContainer .xAuthButtonsContainer .xButtonToolbar').append(compiledTemplate);
        },

        prompt: function() {
          // Hide authenticating elements.
          $('#xUserAuthContainer .xMessagesContainer .activityContainer').hide();

          // Show prompt container.
          $('#xUserAuthContainer .xIntroContainer').show();

          // Show corresponding prompt for strategy.
          var prompt = _lang.userAuth.signIn[_activeAuthStrategyKey + 'AuthPrompt'];
          $('.xIntroContainer .xPrompt').html(prompt);
          $('.xPrompt').show();

          // Show prompt buttons.
          $('#xUserAuthContainer .xAuthButtonsContainer').show();

          // Clear messages.
          view.render.message('');

          // Ensure form is resized.
          view.util.resizeForm();
        },

        message: function(message) {
          // Remove error class.
          $('#xUserAuthContainer .xMessagesContainer').removeClass('xFieldError');

          // Update elements.
          $('#xUserAuthContainer .xMessagesContainer .xMessage').html(message);
          $('#xUserAuthContainer .xMessagesContainer').show();

          // Ensure form is resized.
          view.util.resizeForm();
        },

        error: function(error) {
          // Attach error class.
          $('#xUserAuthContainer .xMessagesContainer').addClass('xFieldError');

          // Update elements.
          $('#xUserAuthContainer .xMessagesContainer .xMessage').html(error);
          $('#xUserAuthContainer .xMessagesContainer').show();

          // Ensure form is resized.
          view.util.resizeForm();
        },

        unsupported: function() {
          // Define overlay config object.
          var overlayConfig = {
            content:
              '<h2>' + _lang.userAuth.signIn.overlayNotice + '</h2><p>' + _lang.userAuth.signIn.unsupported + '</p>',
          };

          // Create overlay.
          NGX.overlay.create(overlayConfig);
        },
      },

      util: {
        populateFormFields: function() {
          var user = _user.get();

          // Pre-fill visible form fields on page >
          $('#nameWrapper .xCompositeItem-FirstName input').val(user.firstName);
          $('#nameWrapper .xCompositeItem-LastName input').val(user.lastName);
          $('#emailWrapper input').val(user.email);

          // Additionally pre-fill hidden form fields >
          var type = 'hidden';
          var formId = 'xCampaignForm';

          //ngxSourceId should be set to the social network identifier of the user or a custom id provided by custom authentication
          _api.setFormField('ngxSourceId', user.id, type, formId);

          // Only set channel field if not already set (eg. by query string arg).
          // Make sure to explicitly check for the existence of an override query string paramater being present, rather than via the current form channel field, as
          // this now seems to be always set to 'website' in the markup returned from the backend.
          var setChannel = true;
          if (_appState.query) {
            if (_appState.query.channel) {
              setChannel = false;
            }
          }
          if (setChannel) {
            //channel should be set to the relevant network (i.e. facebook|twitter|google|instagram) or a custom value specified by custom authentication
            _api.setFormField('channel', user.associatedData.ngxId, type, formId);
          }

          //ngxSourceProfile should be set if available
          if (
            util.isDefined(user.associatedData.profileUsername) &&
            util.hasValue(user.associatedData.profileUsername)
          ) {
            _api.setFormField('ngxSourceProfile', user.associatedData.profileUsername, type, formId);
          }

          //ngxAvatar should be set if available
          if (util.isDefined(user.associatedData.avatarUrl) && util.hasValue(user.associatedData.avatarUrl)) {
            _api.setFormField('ngxAvatar', user.associatedData.avatarUrl, type, formId);
          }

          // Re-trigger form validation if we have previously been asked to verify the users authed status.
          if (_state.verifyAuthedCalled) {
            validate();
          }
        },

        resizeForm: function() {
          try {
            setTimeout(function() {
              _api.resizeParent();
            }, 500);
          } catch (exception) {}
        },
      },
    },
    // Util methods
    util = {
      ucFirst: function capitalize(s) {
        return s[0].toUpperCase() + s.slice(1);
      },
      isDefined: function(value) {
        return typeof value !== 'undefined' ? true : false;
      },
      hasValue: function(value) {
        return !util.isNull(value) && !util.isEmpty(value) ? true : false;
      },
      isNull: function(value) {
        return value === null ? true : false;
      },
      isEmpty: function(value) {
        return value === '' ? true : false;
      },
      validateProvider: function(providerId) {
        var valid = false;

        var idValid = util.validateProviderId(providerId);
        var providerInitialized =
          _state.initializedProviders.indexOf(util.sanitizeProviderId(providerId)) !== -1 ? true : false;

        if (idValid && providerInitialized) {
          valid = true;
        }

        return valid;
      },
      validateProviderId: function(providerId) {
        var valid = false;

        var providerIdSan = util.sanitizeProviderId(providerId);

        if (NGX.Auth.Providers.hasOwnProperty(providerIdSan)) {
          valid = true;
        }

        return valid;
      },
      sanitizeProviderId: function(providerId) {
        const providerIdOut = util.ucFirst(providerId.toLowerCase());

        return providerIdOut;
      },
      processOptions: function(options) {
        // Store reference to callbacks.
        if (options.hasOwnProperty('success')) {
          _callbacks.success = options.success;
        } else {
          delete _callbacks.success;
        }
        if (options.hasOwnProperty('fail')) {
          _callbacks.fail = options.fail;
        } else {
          delete _callbacks.fail;
        }
      },
      campaignFormExists: function() {
        var exists = false;
        var campaignForm = $('#xCampaignForm');
        if (campaignForm.length > 0) {
          exists = true;
        }

        return exists;
      },
      isSupportedBrowser: function() {
        var supported = true;

        try {
          // Get user agent string.
          var ua = navigator.userAgent;

          // Check for Windows Phone <= 8.0 (does not support concept of multiple windows, thus auth window based login flow does not work)
          if (ua.match(/Windows Phone/)) {
            if (ua.match(/Windows Phone 8.0/) || ua.match(/Windows Phone OS 7.5/) || ua.match(/Windows Phone OS 7.0/)) {
              supported = false;
            }
          }
        } catch (exception) {}

        return supported;
      },
      setAuthReady: function() {
        // Send out ready event.
        _api.messageParent({
          action:  'event',
          event:   'auth:ready',
          payload: {},
        });
      },
      appendAuthRedirectParams: function(uri, providerId) {
        uri = uri.replace('&' + _config.authRedirectParams, '');

        if (uri.indexOf('?') !== -1) {
          uri = uri + '&' + _config.authRedirectParams;
        } else {
          uri = uri + '?&' + _config.authRedirectParams;
        }

        uri += '&provider=' + providerId;

        return uri;
      },
    };

  // Public API
  return {
    init:           init,
    enableNetworks: enableNetworks,
    login:          login,
    verifyAuthed:   verifyAuthed,

    Providers: {
      Facebook,
      Instagram,
      Twitter,
      Youtube,
    },
  };
})(window, document, jQuery, _);

// Filter down the full auth config to a set of safe properties that can
// be exposed via x-frame messaging.
export function getSafeAuthConfig() {
  var authConfig = NGX.App.config.socialAuthConfig;
  var config = {};
  if (authConfig) {
    config = {
      strategy:    authConfig.strategy,
      enforcement: authConfig.enforcement,
      display:     authConfig.display,
    };
  }
  return config;
}

// Initialise an auth config appropriate for use with custom sign-in
// to a set of sensible defaults.
export function configureCustomAuthDefaults() {
  // Get existing config.
  var authConfig = NGX.App.config.socialAuthConfig;

  // Custom strategy.
  authConfig.strategy = 'strategy.socialauth.customauthentication';
  // Gated.
  authConfig.display = 'display.socialauth.gate';
  // Force sign-in.
  authConfig.enforcement = 'strategy.socialauthenforcement.required';

  return authConfig;
}
